/**
 * @fileoverview Vue composable for analytics integration
 * Supports Google Analytics 4, custom events, and performance tracking
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/app';

/**
 * Analytics composable for Vue 3
 * @param {Object} config - Analytics configuration
 * @returns {Object} Analytics methods and state
 */
export function useAnalytics(config = {}) {
  const store = useAppStore();

  const defaultConfig = {
    measurementId: config.measurementId || null,
    anonymizeIp: config.anonymizeIp !== false,
    debug: config.debug || false,
    customEndpoint: config.customEndpoint || null,
    autoTrack: config.autoTrack !== false
  };

  const initialized = ref(false);
  const queue = ref([]);
  let scrollTrackingEnabled = false;
  let lastScrollMilestone = 0;

  /**
   * Initialize Google Analytics 4
   * @param {string} measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
   */
  function initGA4(measurementId = defaultConfig.measurementId) {
    if (!measurementId) {
      console.warn('[Analytics] No measurement ID provided');
      return;
    }

    // Load gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configure GA4
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      anonymize_ip: defaultConfig.anonymizeIp,
      debug_mode: defaultConfig.debug
    });

    initialized.value = true;
    console.log('[Analytics] GA4 initialized:', measurementId);

    // Process queued events
    processQueue();
  }

  /**
   * Process queued events
   */
  function processQueue() {
    if (!initialized.value) return;

    queue.value.forEach(event => {
      trackEvent(event.name, event.params);
    });

    queue.value = [];
  }

  /**
   * Track pageview
   * @param {string} [path] - Page path
   * @param {string} [title] - Page title
   */
  function trackPageView(path = window.location.pathname, title = document.title) {
    trackEvent('page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href
    });
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name
   * @param {Object} [params] - Event parameters
   */
  function trackEvent(eventName, params = {}) {
    const event = {
      name: eventName,
      params: {
        ...params,
        timestamp: Date.now()
      }
    };

    if (!initialized.value) {
      queue.value.push(event);
      if (defaultConfig.debug) {
        console.log('[Analytics] Event queued:', event);
      }
      return;
    }

    // Send to GA4
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }

    // Send to custom endpoint
    if (defaultConfig.customEndpoint) {
      sendToCustomEndpoint(event);
    }

    if (defaultConfig.debug) {
      console.log('[Analytics] Event tracked:', event);
    }
  }

  /**
   * Track user interaction
   * @param {string} category - Interaction category
   * @param {string} action - Action taken
   * @param {string} [label] - Optional label
   * @param {number} [value] - Optional numeric value
   */
  function trackInteraction(category, action, label = null, value = null) {
    trackEvent('user_interaction', {
      category,
      action,
      label,
      value
    });
  }

  /**
   * Track external link click
   * @param {string} url - External URL
   * @param {string} [label] - Optional label
   */
  function trackOutboundLink(url, label = null) {
    trackEvent('outbound_link', {
      link_url: url,
      link_domain: new URL(url).hostname,
      label
    });
  }

  /**
   * Track scroll depth
   * @param {number} percentage - Scroll percentage (0-100)
   */
  function trackScrollDepth(percentage) {
    const milestone = Math.floor(percentage / 25) * 25; // 25, 50, 75, 100
    if (milestone > lastScrollMilestone && milestone > 0 && milestone % 25 === 0) {
      lastScrollMilestone = milestone;
      trackEvent('scroll_depth', {
        percent: milestone,
        page: window.location.pathname
      });
    }
  }

  /**
   * Track performance metrics
   * @param {Object} metrics - Performance metrics
   */
  function trackPerformance(metrics) {
    trackEvent('web_vitals', metrics);

    // Update store with performance metrics
    Object.keys(metrics).forEach(key => {
      if (store.performance[key] !== undefined) {
        store.setPerformanceMetric(key, metrics[key]);
      }
    });
  }

  /**
   * Track errors
   * @param {Error} error - Error object
   * @param {string} [context] - Error context
   */
  function trackError(error, context = 'uncaught') {
    trackEvent('error', {
      error_message: error.message,
      error_stack: error.stack,
      error_context: context,
      page: window.location.pathname
    });
  }

  /**
   * Send event to custom endpoint
   * @param {Object} event - Event to send
   */
  function sendToCustomEndpoint(event) {
    if (!defaultConfig.customEndpoint) return;

    const data = JSON.stringify({
      ...event,
      user_agent: navigator.userAgent,
      timestamp: event.params.timestamp || Date.now()
    });

    // Use sendBeacon for reliability
    if (navigator.sendBeacon) {
      navigator.sendBeacon(defaultConfig.customEndpoint, data);
    } else {
      // Fallback to fetch
      fetch(defaultConfig.customEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data,
        keepalive: true
      }).catch(err => console.warn('[Analytics] Failed to send event:', err));
    }
  }

  /**
   * Handle scroll tracking
   */
  function handleScroll() {
    if (!scrollTrackingEnabled) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = window.scrollY;
    const percentage = (scrolled / scrollHeight) * 100;

    trackScrollDepth(Math.min(percentage, 100));
  }

  /**
   * Handle outbound link clicks
   */
  function handleOutboundClick(event) {
    const link = event.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    try {
      const url = new URL(href, window.location.origin);
      if (url.hostname !== window.location.hostname) {
        trackOutboundLink(url.href, link.textContent);
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }

  /**
   * Handle visibility changes
   */
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      trackEvent('page_hidden', {
        page: window.location.pathname,
        time_on_page: Date.now()
      });
    } else {
      trackEvent('page_visible', {
        page: window.location.pathname
      });
    }
  }

  /**
   * Handle global errors
   */
  function handleGlobalError(event) {
    trackError(event.error || new Error(event.message), 'window_error');
  }

  /**
   * Handle unhandled promise rejections
   */
  function handleUnhandledRejection(event) {
    trackError(new Error(event.reason), 'unhandled_rejection');
  }

  /**
   * Enable automatic tracking
   */
  function enableAutoTracking() {
    if (!defaultConfig.autoTrack) return;

    // Track scroll depth
    scrollTrackingEnabled = true;
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Track outbound links
    document.addEventListener('click', handleOutboundClick);

    // Track visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track errors
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    console.log('[Analytics] Auto-tracking enabled');
  }

  /**
   * Disable automatic tracking
   */
  function disableAutoTracking() {
    scrollTrackingEnabled = false;
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleOutboundClick);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);

    console.log('[Analytics] Auto-tracking disabled');
  }

  // Initialize on mount if config provided
  onMounted(() => {
    if (defaultConfig.measurementId) {
      initGA4(defaultConfig.measurementId);
    }
    if (defaultConfig.autoTrack) {
      enableAutoTracking();
    }
  });

  // Cleanup on unmount
  onUnmounted(() => {
    disableAutoTracking();
  });

  return {
    initialized,
    initGA4,
    trackPageView,
    trackEvent,
    trackInteraction,
    trackOutboundLink,
    trackScrollDepth,
    trackPerformance,
    trackError,
    enableAutoTracking,
    disableAutoTracking
  };
}
